import React from 'react';
import { useSweepstakesLeaderboard } from '../../hooks/useSweepstakes';
import { useAuthStore } from '../../store/authStore';
import { WalletAddress } from './WalletAddress';

interface LeaderboardProps {
  contestId: string;
  frozen?: boolean;
}

export const Leaderboard: React.FC<LeaderboardProps> = ({ contestId, frozen = false }) => {
  const { leaderboard, loading } = useSweepstakesLeaderboard(contestId, frozen);
  const { user } = useAuthStore();

  if (loading && !leaderboard) {
    return <div className="text-sm text-subtext-color py-4 text-center">Loading leaderboard...</div>;
  }

  if (!leaderboard || leaderboard.leaderboard.length === 0) {
    return <div className="text-sm text-subtext-color py-4 text-center">No entries yet</div>;
  }

  const isCurrentUser = (addr: string) => user?.xrpl_address === addr;

  return (
    <div>
      {/* Header */}
      <div className="text-xs font-medium text-subtext-color uppercase tracking-wider mb-2 flex">
        <span className="w-12">Rank</span>
        <span className="flex-1">Wallet</span>
        <span className="w-20 text-right">Entries</span>
      </div>

      {/* Rows */}
      <div className="space-y-1">
        {leaderboard.leaderboard.map(row => (
          <div
            key={row.wallet_address}
            className={`flex items-center py-2 px-2 rounded text-sm ${
              isCurrentUser(row.wallet_address)
                ? 'bg-brand-50 border border-brand-200'
                : 'hover:bg-neutral-50'
            }`}
          >
            <span className="w-12 font-semibold text-default-font">
              #{row.rank}
            </span>
            <span className="flex-1">
              <WalletAddress address={row.wallet_address} />
              {isCurrentUser(row.wallet_address) && (
                <span className="ml-2 text-xs text-brand-600 font-medium">(You)</span>
              )}
            </span>
            <span className="w-20 text-right font-semibold text-default-font">
              {row.entry_count}
            </span>
          </div>
        ))}
      </div>

      {/* My Rank (if not in top 20) */}
      {leaderboard.myRank && !leaderboard.leaderboard.some(r => isCurrentUser(r.wallet_address)) && (
        <div className="mt-3 pt-3 border-t border-neutral-border">
          <div className="flex items-center py-2 px-2 rounded bg-brand-50 border border-brand-200 text-sm">
            <span className="w-12 font-semibold text-default-font">
              #{leaderboard.myRank.rank}
            </span>
            <span className="flex-1">
              <WalletAddress address={user?.xrpl_address || ''} />
              <span className="ml-2 text-xs text-brand-600 font-medium">(You)</span>
            </span>
            <span className="w-20 text-right font-semibold text-default-font">
              {leaderboard.myRank.entry_count}
            </span>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="mt-3 pt-3 border-t border-neutral-border flex gap-4 text-xs text-subtext-color">
        <span>{leaderboard.totalParticipants} participants</span>
        <span>{leaderboard.totalEntries} total entries</span>
        {frozen && <span className="ml-auto italic">Final results</span>}
      </div>
    </div>
  );
};
